import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { appendAuditLogEntry } from '../../utils/auditLog';
import { resolveAdminRole } from '../../utils/adminRole';
import {
  getContactDetails,
  resetContactDetails,
  saveContactDetails,
} from '../../utils/contactDetails';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

const ContactDetailsManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [details, setDetails] = useState(() => getContactDetails());
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState('');

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate('/login');
          return;
        }

        const { data: isAdmin, error: rpcError } = await supabase.rpc('check_is_admin', {
          p_email: session.user.email,
        });

        if (rpcError || !isAdmin) {
          navigate('/account');
          return;
        }

        await resolveAdminRole(session.user.email);

        setAdminInfo({
          email: session.user.email,
          lastSignIn: session.user.last_sign_in_at,
        });
      } catch (err) {
        console.error('Contact details admin access check error:', err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [navigate]);

  const updateSectionField = (section, key, value) => {
    setDetails((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: value,
      },
    }));
  };

  const updateHour = (section, index, value) => {
    setDetails((current) => ({
      ...current,
      [section]: {
        ...current[section],
        hours: current[section].hours.map((entry, hourIndex) => (
          hourIndex === index ? { ...entry, hours: value } : entry
        )),
      },
    }));
  };

  const handleSaveRequest = (event) => {
    event.preventDefault();
    setShowConfirmSave(true);
  };

  const handleSave = () => {
    setError('');
    setMessage('');
    setShowConfirmSave(false);

    try {
      saveContactDetails(details);
      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Updated',
        entityType: 'contact details',
        entityName: 'Trust and Social contact details',
        details: 'Updated public trust and social contact details.',
      });
      setLastSavedAt(new Date().toLocaleString('en-GB'));
      setMessage('Contact details published to the website successfully.');
    } catch (err) {
      console.error('Unable to save contact details:', err);
      setError(err.message || 'Unable to save contact details.');
    }
  };

  const handleReset = () => {
    resetContactDetails();
    setDetails(getContactDetails());
    setMessage('Contact details restored to defaults.');
    setError('');
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={styles.mutedText}>Loading contact details editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logo} />
        </div>

        <div style={styles.header}>
          <div style={styles.kicker}>Admin tools</div>
          <h2 style={styles.title}>Contact Details Editor</h2>
          <p style={styles.subtitle}>Update the public Trust and Social contact details from one place.</p>
        </div>

        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Signed in as</span>
          <span style={styles.infoValue}>{adminInfo?.email}</span>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}
        {message && <div style={styles.successBanner}>{message}</div>}
        {lastSavedAt && <div style={styles.publishBanner}>Last published: {lastSavedAt}</div>}

        <form onSubmit={handleSaveRequest}>
          <div style={styles.sectionGrid}>
            <section style={styles.panel}>
              <h3 style={styles.sectionHeading}>Trust contact details</h3>
              <div style={styles.fieldGrid}>
                <label style={styles.field}>
                  <span>Address</span>
                  <textarea rows="3" value={details.trust.address} onChange={(event) => updateSectionField('trust', 'address', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>Phone</span>
                  <input value={details.trust.phone} onChange={(event) => updateSectionField('trust', 'phone', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>Email</span>
                  <input type="email" value={details.trust.email} onChange={(event) => updateSectionField('trust', 'email', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>Email subject</span>
                  <input value={details.trust.emailSubject} onChange={(event) => updateSectionField('trust', 'emailSubject', event.target.value)} />
                </label>
                <label style={styles.fieldFull}>
                  <span>Email body</span>
                  <textarea rows="4" value={details.trust.emailBody} onChange={(event) => updateSectionField('trust', 'emailBody', event.target.value)} />
                </label>
              </div>

              <div style={styles.hoursBox}>
                <h4 style={styles.hoursHeading}>Trust opening hours</h4>
                <div style={styles.hoursList}>
                  {details.trust.hours.map((entry, index) => (
                    <div key={entry.day} style={styles.hourRow}>
                      <span style={styles.hourDay}>{entry.day}</span>
                      <input value={entry.hours} onChange={(event) => updateHour('trust', index, event.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section style={styles.panel}>
              <h3 style={styles.sectionHeading}>Social contact details</h3>
              <div style={styles.fieldGrid}>
                <label style={styles.field}>
                  <span>Address</span>
                  <textarea rows="3" value={details.social.address} onChange={(event) => updateSectionField('social', 'address', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>Phone</span>
                  <input value={details.social.phone} onChange={(event) => updateSectionField('social', 'phone', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>Email</span>
                  <input type="email" value={details.social.email} onChange={(event) => updateSectionField('social', 'email', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>Email subject</span>
                  <input value={details.social.emailSubject} onChange={(event) => updateSectionField('social', 'emailSubject', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>Office hours label</span>
                  <input value={details.social.officeHoursLabel} onChange={(event) => updateSectionField('social', 'officeHoursLabel', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>Office hours time</span>
                  <input value={details.social.officeHours} onChange={(event) => updateSectionField('social', 'officeHours', event.target.value)} />
                </label>
                <label style={styles.fieldFull}>
                  <span>Email body</span>
                  <textarea rows="4" value={details.social.emailBody} onChange={(event) => updateSectionField('social', 'emailBody', event.target.value)} />
                </label>
              </div>

              <div style={styles.hoursBox}>
                <h4 style={styles.hoursHeading}>Social club opening hours</h4>
                <div style={styles.hoursList}>
                  {details.social.hours.map((entry, index) => (
                    <div key={entry.day} style={styles.hourRow}>
                      <span style={styles.hourDay}>{entry.day}</span>
                      <input value={entry.hours} onChange={(event) => updateHour('social', index, event.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <div style={styles.actions}>
            <button type="submit" style={styles.primaryBtn}>Review changes</button>
            <button type="button" style={styles.secondaryBtn} onClick={handleReset}>Reset to defaults</button>
            <button type="button" style={styles.ghostBtn} onClick={() => navigate('/admin-account')}>Back to dashboard</button>
          </div>
        </form>
      </div>

      {showConfirmSave && (
        <div style={styles.modalBackdrop} role="presentation" onClick={() => setShowConfirmSave(false)}>
          <div style={styles.modalCard} role="dialog" aria-modal="true" aria-labelledby="contact-details-confirm-title" onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalKicker}>Confirm publish</div>
            <h3 id="contact-details-confirm-title" style={styles.modalTitle}>Publish updated contact details?</h3>
            <p style={styles.modalText}>
              This will update the Trust and Social contact information shown on the public website.
              Check the details below before confirming.
            </p>
            <div style={styles.modalSummary}>
              <div><strong>Trust email:</strong> {details.trust.email}</div>
              <div><strong>Social email:</strong> {details.social.email}</div>
              <div><strong>Trust phone:</strong> {details.trust.phone}</div>
              <div><strong>Social phone:</strong> {details.social.phone}</div>
            </div>
            <div style={styles.modalActions}>
              <button type="button" style={styles.primaryBtn} onClick={handleSave}>Yes, publish changes</button>
              <button type="button" style={styles.secondaryBtn} onClick={() => setShowConfirmSave(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 20px)',
    background: 'linear-gradient(180deg, #f8fafc 0%, #ecfdf5 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #dbe7df',
    borderRadius: '16px',
    padding: 'clamp(18px, 4vw, 40px)',
    width: '100%',
    maxWidth: '1180px',
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.08)',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logo: {
    height: 'clamp(60px, 12vw, 80px)',
    width: 'auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '22px',
  },
  kicker: {
    display: 'inline-block',
    background: 'rgba(22, 101, 52, 0.08)',
    color: '#166534',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    padding: '4px 12px',
    borderRadius: '20px',
    marginBottom: '10px',
    textTransform: 'uppercase',
  },
  title: {
    color: '#0f172a',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  subtitle: {
    color: '#475569',
    fontSize: '14px',
    margin: 0,
  },
  infoRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '20px',
    color: '#0f172a',
  },
  infoLabel: {
    fontSize: '12px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#166534',
  },
  infoValue: {
    fontWeight: '700',
    wordBreak: 'break-word',
  },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '18px',
  },
  panel: {
    background: '#f8fafc',
    border: '1px solid #dbe7df',
    borderRadius: '12px',
    padding: '18px',
  },
  sectionHeading: {
    color: '#0f172a',
    fontSize: '16px',
    fontWeight: '700',
    margin: '0 0 12px',
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '12px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#0f172a',
    fontSize: '13px',
  },
  fieldFull: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#0f172a',
    fontSize: '13px',
  },
  hoursBox: {
    marginTop: '16px',
    padding: '14px',
    background: '#ffffff',
    border: '1px solid #dbe7df',
    borderRadius: '12px',
  },
  hoursHeading: {
    color: '#166534',
    margin: '0 0 10px',
    fontSize: '14px',
  },
  hoursList: {
    display: 'grid',
    gap: '8px',
  },
  hourRow: {
    display: 'grid',
    gridTemplateColumns: '110px minmax(0, 1fr)',
    gap: '10px',
    alignItems: 'center',
  },
  hourDay: {
    color: '#334155',
    fontSize: '13px',
    fontWeight: '600',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '18px',
  },
  primaryBtn: {
    padding: '14px 16px',
    background: '#166534',
    color: '#ecfdf5',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '14px 16px',
    background: '#f8fafc',
    color: '#166534',
    border: '1px solid #bbd3c2',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  ghostBtn: {
    padding: '14px 16px',
    background: '#ffffff',
    color: '#0f172a',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  mutedText: {
    color: '#475569',
    textAlign: 'center',
    margin: 0,
  },
  errorBanner: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b',
    padding: '12px 14px',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  successBanner: {
    background: '#ecfdf5',
    border: '1px solid #bbf7d0',
    color: '#166534',
    padding: '12px 14px',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  publishBanner: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1d4ed8',
    padding: '12px 14px',
    borderRadius: '8px',
    marginBottom: '12px',
    fontWeight: '600',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 50,
  },
  modalCard: {
    width: '100%',
    maxWidth: '640px',
    background: '#ffffff',
    borderRadius: '18px',
    padding: '24px',
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
  },
  modalKicker: {
    display: 'inline-block',
    background: 'rgba(22, 101, 52, 0.08)',
    color: '#166534',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    padding: '4px 12px',
    borderRadius: '999px',
    textTransform: 'uppercase',
    marginBottom: '10px',
  },
  modalTitle: {
    margin: '0 0 10px',
    color: '#0f172a',
    fontSize: '22px',
  },
  modalText: {
    margin: '0 0 16px',
    color: '#475569',
    lineHeight: 1.6,
  },
  modalSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '10px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '18px',
    color: '#0f172a',
  },
  modalActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
};

export default ContactDetailsManagement;